import { User } from '@/services/Api/index';
import StorageHelper from './storageHelper';

let inFlightPromise = null;
let lastRetryTimestamp = 0;
let FALLBACK_RETRY_WINDOW_MS = 30 * 1000;

/**
 * Initialize chatbot - fetches data only if not in localStorage
 * Returns the same promise if already initializing (prevents duplicate calls)
 */
export async function initChatbot(store) {
  const isChatbotInitializing = store.getters['app/getIsChatbotInitializing'];

  if (isChatbotInitializing && inFlightPromise) {
    return inFlightPromise;
  }

  store.dispatch('app/setIsChatbotInitializing', true);

  inFlightPromise = (async () => {
    let willRetry = false;

    try {
      let chatbot = new StorageHelper({
        id: process.env.VUE_APP_STORAGE_CHATBOT_MODEL,
      }).get();
      let chatbotScript = new StorageHelper({ id: 'ChatbotScript' }).get();

      if (!chatbot) {
        const chatbotModelRes = await User.getChatbotModel();
        if (!chatbotModelRes) {
          throw new Error('[ChatbotInit] chatbot model request failed - no response');
        }

        chatbot = chatbotModelRes?.data?.Data?.chatbot;
        if (!chatbot) throw new Error('[ChatbotInit] chatbot model not found');

        new StorageHelper({
          id: process.env.VUE_APP_STORAGE_CHATBOT_MODEL,
        }).set(chatbot);
      }

      if (!chatbot.scriptUrl)
        throw new Error('[ChatbotInit] scriptUrl is not found in chatbotModel');

      if (!chatbotScript) {
        new StorageHelper({ id: 'ChatbotScript' }).set(chatbot?.scriptUrl);
      }

      const user = new StorageHelper({ id: process.env.VUE_APP_STORAGE_CURRENT_USER_KEY }).get();

      if (!user) throw new Error('[ChatbotInit] user not found');

      const chatbotDataRes = await User.getChatbotData();
      if (!chatbotDataRes) {
        throw new Error('[ChatbotInit] chatbotdata request failed - no response');
      }

      const chatBotData = chatbotDataRes?.data?.Data?.chatBotData;
      if (!chatBotData) throw new Error('[ChatbotInit] chatBotData is missing');

      new StorageHelper({ id: process.env.VUE_APP_STORAGE_CHATBOT_DATA }).set(chatBotData);

      // Set global variables
      window._sorun_set_variable = JSON.stringify(chatBotData);
      window._sorun_set_mobile_in_iframe = 'true';
      window._sorun_set_name = chatbot.firstName;
      window._sorun_set_surname = chatbot.lastName;
      window._sorun_set_companyID = chatbot.companyId;
      window._sorun_set_lang = 'tr';
      window._sorun_set_token = chatbot.chatToken;
      window._sorun_set_mobileIframeOnloadDisabled = 'true';
      window.mbHideIcon = false;

      window.MB_CLIENT = user.id;
      window.MB_TOKEN = chatbot.chatToken;
      window.MB_VARIABLE = chatBotData;

      if (chatbot.poolFilter && chatbot.poolFilter !== 'undefined') {
        window._sorun_set_poolFilter = chatbot.poolFilter;
      }
      if (chatbot.autoOpen) {
        window._sorun_set_auto_open_delay = 1;
      }

      // load script if not already loaded
      if (!document.getElementById('mb-chatbot-script')) {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.id = 'mb-chatbot-script';
          script.src = chatbot.scriptUrl;
          script.onload = resolve;
          script.onerror = () => reject(new Error('chatbot script load failed'));
          document.head.append(script);
        });
      }

      willRetry = false;
    } catch (error) {
      const now = Date.now();
      const canRetry = now - lastRetryTimestamp > FALLBACK_RETRY_WINDOW_MS;

      if (canRetry) {
        willRetry = true;
        lastRetryTimestamp = now;
        inFlightPromise = null;
        console.warn('[ChatbotInit] Retrying init once (fallback)');
        return initChatbot(store);
      }

      willRetry = false;
      throw error;
    } finally {
      if (!willRetry) {
        store.dispatch('app/setIsChatbotInitializing', false);
        inFlightPromise = null;
      }
    }
  })();

  return inFlightPromise;
}

export function hasChatbotRuntimeData() {
  let chatbot = new StorageHelper({
    id: process.env.VUE_APP_STORAGE_CHATBOT_MODEL,
  }).get();
  let chatbotScript = new StorageHelper({ id: 'ChatbotScript' }).get();
  const user = new StorageHelper({ id: process.env.VUE_APP_STORAGE_CURRENT_USER_KEY }).get();
  const chatbotData = new StorageHelper({
    id: process.env.VUE_APP_STORAGE_CHATBOT_DATA,
  }).get();

  return !!(chatbot && chatbotScript && chatbotData && user);
}

export function hideChatbotWidget() {
  if (document.getElementById('mb-webchat-visibility')) return;

  const style = document.createElement('style');
  style.id = 'mb-webchat-visibility';
  style.textContent = `
    #MB_WEBCHAT_WIDGET {
      display: none !important;
    }
  `;
  document.head.appendChild(style);
}

export function showChatbotWidget() {
  const style = document.getElementById('mb-webchat-visibility');
  if (style) style.remove();
}
